export const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    if (window.location.port && window.location.port !== '5000') {
      return `${window.location.protocol}//${window.location.hostname}:5000`;
    }
  }
  return '';
})();

export const normalizeEmail = (value = '') => value.trim().toLowerCase();

export async function loginUserApi({ email, password }) {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizeEmail(email), password }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Login failed');
  }
  return data;
}

export async function registerUserApi({ email, password, fullName, phone }) {
  const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizeEmail(email), password, fullName, phone }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Registration failed');
  }
  return data;
}

export async function sendOtpApi({ email }) {
  const response = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizeEmail(email) }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Failed to send verification code');
  }
  return data;
}

export async function verifyOtpApi({ email, otp }) {
  const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizeEmail(email), otp }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Email verification failed');
  }
  return data;
}

export async function sendMobileOtpApi({ phone }) {
  const response = await fetch(`${API_BASE_URL}/api/auth/send-mobile-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: phone.trim() }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Failed to send mobile verification code');
  }
  return data;
}

export async function verifyMobileOtpApi({ phone, otp }) {
  const response = await fetch(`${API_BASE_URL}/api/auth/verify-mobile-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: phone.trim(), otp }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Mobile verification failed');
  }
  return data;
}

export async function uploadImageApi(file, metadata = {}) {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('file', file);
  if (metadata && metadata.variants) {
    if (metadata.variants.large) formData.append('image_800', metadata.variants.large);
    if (metadata.variants.medium) formData.append('image_300', metadata.variants.medium);
    if (metadata.variants.thumb) formData.append('image_150', metadata.variants.thumb);
  }
  formData.append('metadata', JSON.stringify(metadata));

  const response = await fetch(`${API_BASE_URL}/api/uploads`, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Upload failed');
  }
  return data;
}

export async function uploadVideoApi(file, metadata = {}) {
  const formData = new FormData();
  formData.append('video', file);
  formData.append('file', file);
  formData.append('metadata', JSON.stringify(metadata));

  const response = await fetch(`${API_BASE_URL}/api/uploads`, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Video upload failed');
  }
  return data;
}

export async function createProductApi(product) {
  const response = await fetch(`${API_BASE_URL}/api/products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(product),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Create product failed');
  }
  return data;
}

export async function fetchProductsApi() {
  const response = await fetch(`${API_BASE_URL}/api/products`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fetch products failed');
  }
  return data;
}

export async function checkUserExistsApi({ email, phone }) {
  const params = new URLSearchParams();
  if (email) params.append('email', email);
  if (phone) params.append('phone', phone);
  
  const response = await fetch(`${API_BASE_URL}/api/auth/check?${params.toString()}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Check user failed');
  }
  return data;
}

export async function syncUserDataApi({ email, cart, wishlist, addresses }) {
  const response = await fetch(`${API_BASE_URL}/api/users/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, cart, wishlist, addresses }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Sync failed');
  }
  return data;
}

export async function createOrderApi(order) {
  const response = await fetch(`${API_BASE_URL}/api/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(order),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Create order failed');
  }
  return data;
}

export async function fetchOrdersApi(email) {
  const url = email ? `${API_BASE_URL}/api/orders?email=${encodeURIComponent(email)}` : `${API_BASE_URL}/api/orders`;
  const response = await fetch(url);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fetch orders failed');
  }
  return data;
}

export async function updateOrderStatusApi(orderId, status) {
  const response = await fetch(`${API_BASE_URL}/api/orders/${encodeURIComponent(orderId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Update order status failed');
  }
  return data;
}

export async function updateProductApi(id, product) {
  const response = await fetch(`${API_BASE_URL}/api/products/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(product),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Update product failed');
  }
  return data;
}

export async function deleteProductApi(id) {
  const response = await fetch(`${API_BASE_URL}/api/products/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Delete product failed');
  }
  return data;
}

export async function deleteOrderApi(orderId) {
  const response = await fetch(`${API_BASE_URL}/api/orders/${encodeURIComponent(orderId)}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Delete order failed');
  }
  return data;
}

export async function fetchVideosApi() {
  const response = await fetch(`${API_BASE_URL}/api/videos`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fetch videos failed');
  }
  return data;
}

export async function createVideoApi(video) {
  const response = await fetch(`${API_BASE_URL}/api/videos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(video),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Create video failed');
  }
  return data;
}

export async function updateVideoApi(id, video) {
  const response = await fetch(`${API_BASE_URL}/api/videos/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(video),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Update video failed');
  }
  return data;
}

export async function deleteVideoApi(id) {
  const response = await fetch(`${API_BASE_URL}/api/videos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Delete video failed');
  }
  return data;
}

// Razorpay Payment Gateway APIs
export async function fetchPaymentConfigApi() {
  const response = await fetch(`${API_BASE_URL}/api/payment/config`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fetch payment config failed');
  }
  return data;
}

export async function createRazorpayOrderApi({ amount, currency = 'INR', receipt, notes = {} }) {
  const response = await fetch(`${API_BASE_URL}/api/payment/razorpay/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount, currency, receipt, notes }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Create Razorpay order failed');
  }
  return data;
}

export async function verifyRazorpayPaymentApi({
  razorpay_order_id,
  razorpay_payment_id,
  razorpay_signature,
  orderDetails = null
}) {
  const response = await fetch(`${API_BASE_URL}/api/payment/razorpay/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      orderDetails
    }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Payment signature verification failed');
  }
  return data;
}

export async function createRazorpayPaymentLinkApi({ amount, description, customer, reference_id }) {
  const response = await fetch(`${API_BASE_URL}/api/payment/razorpay/create-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount, description, customer, reference_id }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Create Razorpay link failed');
  }
  return data;
}

// Blogs and Articles APIs
export async function fetchBlogsApi() {
  const response = await fetch(`${API_BASE_URL}/api/blogs`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fetch blogs failed');
  }
  return data;
}

export async function fetchBlogByIdApi(idOrSlug) {
  const response = await fetch(`${API_BASE_URL}/api/blogs/${encodeURIComponent(idOrSlug)}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fetch blog post failed');
  }
  return data;
}

export async function createBlogApi(blog) {
  const response = await fetch(`${API_BASE_URL}/api/blogs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(blog),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Create blog post failed');
  }
  return data;
}

export async function updateBlogApi(id, blog) {
  const response = await fetch(`${API_BASE_URL}/api/blogs/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(blog),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Update blog post failed');
  }
  return data;
}

export async function deleteBlogApi(id) {
  const response = await fetch(`${API_BASE_URL}/api/blogs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Delete blog post failed');
  }
  return data;
}

// Contributors APIs
export async function fetchContributorsApi() {
  const response = await fetch(`${API_BASE_URL}/api/contributors`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fetch contributors failed');
  }
  return data;
}

export async function createContributorApi(contributor) {
  const response = await fetch(`${API_BASE_URL}/api/contributors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contributor),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Create contributor failed');
  }
  return data;
}

export async function updateContributorApi(id, contributor) {
  const response = await fetch(`${API_BASE_URL}/api/contributors/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contributor),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Update contributor failed');
  }
  return data;
}

export async function deleteContributorApi(id) {
  const response = await fetch(`${API_BASE_URL}/api/contributors/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Delete contributor failed');
  }
  return data;
}

